/**
 * @typedef {("dropdown"|"radio"|"tag"|"checkbox"|"text"|"number"|"date")} CustomFieldType
 */
export const customFieldTypes = {
    DROPDOWN: 'dropdown',
    RADIO: 'radio',
    TAG: 'tag',
    CHECKBOX: 'checkbox',
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
};

/* eslint-disable max-len, vue/max-len */
/**
 * Represents formatting rules for a custom field's content.
 * @typedef {Object} FieldFormat
 * @property {CustomFieldType} type - The type of the custom field
 * @property {number} minValue - The minimum value for the field content. Might be length for text, or value for numbers.
 * @property {number} minValue - The maximum length for the field content. Might be length for text, or value for numbers.
 */

/**
 * Represents options for a custom field that can be selectable, such as in a dropdown or radio group.
 * @typedef {Object} CustomFieldOption
 * @property {string} value - The value of the option, which is submitted when the option is selected.
 * @property {string} label - The localized label of the option.
 * @property {boolean} checked - Indicates whether the option is currently selected or not.
 */

/**
 * Represents a localized value for a particular locale, with a value that can be a string or null.
 * @typedef {Object} LocaleValue
 * @property {string|null} value - The textual content for a specific locale, or null if not specified.
 * @property {string} locale - The locale code associated with the value (e.g., 'en', 'de').
 */

/**
 * Represents options for a custom field that can be selectable, such as in a dropdown or radio group.
 * @typedef {Object} CustomFieldOption
 * @property {string} value - The value of the option, which is submitted when the option is selected.
 * @property {string} label - The localized label of the option.
 * @property {boolean} checked - Indicates whether the option is currently selected or not.
 */

/**
 * Represents a response from a backend-for-frontend (BFF) service containing
 * the configuration for custom fields of various types.
 * @typedef {Object[]} BFFCustomFieldResponse
 * @property {string} type - The overall grouping type for the custom fields, e.g. 'customFields'.
 * @property {number} companyId - The identifier of the company associated with the custom field.
 * @property {string} id - A unique identifier for the custom field.
 * @property {LocaleValue[]} label - An array of objects representing the label of the custom field in different locales.
 * @property {LocaleValue[]} placeholder - An array of objects representing the placeholder of the custom field in different locales; can be null.
 * @property {boolean} required - Indicates whether the custom field is required or not.
 * @property {FieldFormat} format - Defines the formatting rules for the custom field's content.
 * @property {CustomFieldOption[]|null} options - An array of option objects for the custom field, or null if not applicable.
 */

/**
 * Represents a localized custom field with additional properties such as unchecked options and an undefined value field.
 * @typedef {Object} CustomField
 * @property {CustomFieldType} type - The type of the custom field
 * @property {number} minValue - The minimum length for the field content.
 * @property {number} maxValue - The maximum length for the field content.
 * @property {number} companyId - The company ID, as defined in BFFCustomFieldResponse.
 * @property {string} id - The field ID, as defined in BFFCustomFieldResponse.
 * @property {string} label - The localized label of the custom field.
 * @property {string|null} placeholder - The localized placeholder for the custom field, if applicable; can be null.
 * @property {boolean} required - Indicates whether the custom field is required or not, as defined in BFFCustomFieldResponse.
 * @property {string|number|boolean|string[]|null|undefined} value - The value of the custom field. This will be undefined initially but can vary depending on the type defined in format.
 * @property {CustomFieldOption[]|null} options - An array of option objects for the custom field, if applicable; can be null.
 */
/* eslint-enable max-len, vue/max-len */

function getLocaleValue(array, locale) {
    const found = array.find(element => element.locale === locale);
    return found ? found.value : '';
}

/* Hard code the locale until plans of i18n get more concrete */
const locale = 'de';

/**
 * Transforms a raw BFF custom field response into an array of CustomField objects
 * with localized labels, placeholders, and options. Unchecked options and undefined
 * value fields are initialized.
 *
 * @param {BFFCustomFieldResponse} fieldResponse - The raw custom field response from BFF.
 * @returns {CustomField[]} An array of CustomField objects with additional properties
 *                          such as a localized label, placeholder, and options.
 */
export function prepareCustomFields(fieldResponse) {
    return fieldResponse.map(customField => ({
        ...customField,
        minValue: customField.format.minValue ?? null,
        maxValue: customField.format.maxValue ?? null,
        type: customField.format.type,
        label: getLocaleValue(customField.label, locale),
        placeholder: getLocaleValue(customField.placeholder, locale),
        options: customField.options?.map(option => ({
            value: option.id,
            label: getLocaleValue(option.label, locale),
        })) ?? null,
        value: null,
    }));
}

export const customFieldProps = {
    label: {
        type: String,
        required: true,
    },
    maxValue: {
        type: Number,
    },
    minValue: {
        type: Number,
    },
    options: {
        type: Array,
    },
    id: {
        type: String,
    },
    placeholder: {
        type: String,
    },
    required: {
        type: Boolean,
    },
    type: {
        type: String,
    },
    value: {
        type: [String, Array, Number],
    },
};
